import { DataProvider } from '@refinedev/core';
import { apiClient } from '@src/utils/axios';
import { dayjs, startOfDay, endOfDay } from '@src/utils/dayjs';
import { PATIENT_SEARCH_MATCH_ANY_SENTINEL } from '@src/utils/patientSearchFilters';

export const dataProvider: DataProvider = {
  getList: async ({ resource, pagination, filters, sorters }) => {
    const url = `/${resource}`;

    const { current = 1, pageSize = 10, mode } = pagination ?? {};

    const queryFilters = filters?.reduce((acc, filter) => {
      // Only process LogicalFilter types (not ConditionalFilter)
      if ('field' in filter) {
        const logicalFilter = filter as any;

        // Skip only if value is null or undefined, but allow empty strings and empty arrays
        if (logicalFilter.value === null || logicalFilter.value === undefined) {
          return acc;
        }

        // For 'in' operator, allow empty arrays but skip if it's an empty string
        if (logicalFilter.operator === 'in' && Array.isArray(logicalFilter.value) && logicalFilter.value.length === 0) {
          return acc;
        }

        // For other operators, skip empty strings
        if (logicalFilter.operator !== 'in' && logicalFilter.value === '') {
          return acc;
        }
        // Handle date fields (created_at, createdAt, updated_at, etc.)
        const isDateField =
          logicalFilter.field.includes('updated_at') ||
          logicalFilter.field.includes('updatedAt') ||
          logicalFilter.field.includes('date');

        // Handle datetime fields (follow_up_date, scheduled_datetime, etc.)
        const isDateTimeField =
          logicalFilter.field.includes('follow_up_date') ||
          logicalFilter.field.includes('scheduled_datetime') ||
          logicalFilter.field.includes('created_at');


        if (logicalFilter.operator === 'eq') {
          // Handle nested fields like medicine.name
          const fieldName = logicalFilter.field.includes('.')
            ? logicalFilter.field.replace('.', '_')
            : logicalFilter.field;

          if (isDateTimeField) {
            acc[fieldName] = dayjs(logicalFilter.value)
              .utc()
              .format('YYYY-MM-DDTHH:mm:ss.SSS[Z]'); // Full datetime format in UTC
          } else if (isDateField) {
            acc[fieldName] = dayjs(logicalFilter.value)
              .utc()
              .format('YYYY-MM-DD'); // YYYY-MM-DD format in UTC
          } else {
            // For regular fields, use the field name directly
            acc[fieldName] = logicalFilter.value;
          }
        } else if (logicalFilter.operator === 'contains') {
          // For contains, use LIKE operator with wildcards
          // Handle nested fields like medicine.name
          const fieldName = logicalFilter.field.includes('.')
            ? logicalFilter.field.replace('.', '_')
            : logicalFilter.field;
          // Patient last_name: match any when only one name token was typed (see patientSearchFilters)
          if (logicalFilter.value === PATIENT_SEARCH_MATCH_ANY_SENTINEL) {
            acc[`${fieldName}_like`] = '%';
          } else {
            acc[`${fieldName}_like`] = `%${logicalFilter.value}%`;
          }
        } else if (logicalFilter.operator === 'gte') {
          // Handle nested fields like medicine.name
          const fieldName = logicalFilter.field.includes('.')
            ? logicalFilter.field.replace('.', '_')
            : logicalFilter.field;

          if (isDateTimeField) {
            const startDateTime = dayjs(logicalFilter.value)
              .startOf('day')
              .utc()
              .format('YYYY-MM-DD HH:mm:ss'); // MySQL compatible datetime format
            acc[`${fieldName}_gte`] = startDateTime; // Full datetime format in UTC
          }
          else if (isDateField) {
            const startDate = startOfDay(logicalFilter.value);
            const formattedDate = dayjs(startDate).utc().format('YYYY-MM-DD');
            acc[`${fieldName}_gte`] = formattedDate; // YYYY-MM-DD format with double underscore in UTC
          } else {
            acc[`${fieldName}_gte`] = logicalFilter.value;
          }
        } else if (logicalFilter.operator === 'lte') {
          // Handle nested fields like medicine.name
          const fieldName = logicalFilter.field.includes('.')
            ? logicalFilter.field.replace('.', '_')
            : logicalFilter.field;

          if (isDateTimeField) {
            const endDateTime = dayjs(logicalFilter.value)
              .endOf('day')
              .utc()
              .format('YYYY-MM-DD HH:mm:ss'); // MySQL compatible datetime format
            acc[`${fieldName}_lte`] = endDateTime; // Full datetime format in UTC
          } else if (isDateField) {
            const endDate = endOfDay(logicalFilter.value);
            const formattedDate = dayjs(endDate).utc().format('YYYY-MM-DD');
            acc[`${fieldName}_lte`] = formattedDate; // YYYY-MM-DD format with double underscore in UTC
          } else {
            acc[`${fieldName}_lte`] = logicalFilter.value;
          }
        } else if (logicalFilter.operator === 'gt') {
          // Handle nested fields like medicine.name
          const fieldName = logicalFilter.field.includes('.')
            ? logicalFilter.field.replace('.', '_')
            : logicalFilter.field;
          acc[`${fieldName}_gt`] = logicalFilter.value;
        } else if (logicalFilter.operator === 'lt') {
          // Handle nested fields like medicine.name
          const fieldName = logicalFilter.field.includes('.')
            ? logicalFilter.field.replace('.', '_')
            : logicalFilter.field;
          acc[`${fieldName}_lt`] = logicalFilter.value;
        } else if (logicalFilter.operator === 'in') {
          // Handle nested fields like category.id
          const fieldName = logicalFilter.field.includes('.')
            ? logicalFilter.field.replace('.', '_')
            : logicalFilter.field;

          // For 'in' operator, handle both array and string values
          if (Array.isArray(logicalFilter.value)) {
            // Skip empty arrays
            if (logicalFilter.value.length > 0) {
              acc[`${fieldName}_in`] = logicalFilter.value.join(',');
            }
          } else if (typeof logicalFilter.value === 'string' && logicalFilter.value.includes(',')) {
            // If it's already a comma-separated string, use it as is
            acc[`${fieldName}_in`] = logicalFilter.value;
          } else if (logicalFilter.value !== '') {
            // Single value, treat as array with one item (but skip empty strings)
            acc[`${fieldName}_in`] = logicalFilter.value;
          }
        } else if (logicalFilter.operator === 'nin') {
          // Handle nested fields like medicine.name
          const fieldName = logicalFilter.field.includes('.')
            ? logicalFilter.field.replace('.', '_')
            : logicalFilter.field;
          acc[`${fieldName}_nin`] = Array.isArray(logicalFilter.value)
            ? logicalFilter.value.join(',')
            : logicalFilter.value;
        } else if (logicalFilter.operator === 'startswith') {
          // Handle nested fields like medicine.name
          const fieldName = logicalFilter.field.includes('.')
            ? logicalFilter.field.replace('.', '_')
            : logicalFilter.field;
          acc[`${fieldName}_like`] = `${logicalFilter.value}%`;
        } else if (logicalFilter.operator === 'endswith') {
          // Handle nested fields like medicine.name
          const fieldName = logicalFilter.field.includes('.')
            ? logicalFilter.field.replace('.', '_')
            : logicalFilter.field;
          acc[`${fieldName}_like`] = `%${logicalFilter.value}`;
        }
      }
      return acc;
    }, {} as any);

    // Handle sorting - single sorter only
    // API Format: ?_sort=fieldName&_order=asc|desc
    let sortQuery = {};
    if (sorters && sorters.length > 0) {
      // Take the first sorter (most recent)
      const sorter = sorters[0];
      if (sorter.field && sorter.order) {
        sortQuery = {
          sort: sorter.field,
          order: sorter.order,
        };
      }
    }


    // Build query object - exclude pagination if mode is 'off'
    const query: any = {
      ...queryFilters,
      ...sortQuery,
    };

    // Only add pagination parameters if mode is not 'off'
    if (mode !== 'off') {
      query.page = current;
      query.limit = pageSize;
    }
    const { data, headers } = await apiClient.get(url, { params: query });

    const total = headers['x-total-count']
      ? parseInt(headers['x-total-count'])
      : data.length;

    // Transform text fields to handle escaped newlines in list data
    let transformedData = data.data;
    if (Array.isArray(transformedData)) {
      transformedData = transformedData.map(item => {
        const textFields = [
          'notes', 'lab_report', 'today_procedure', 'remark', 'description'
        ];
        textFields.forEach(field => {
          if (item[field] && typeof item[field] === 'string') {
            // Handle multiple levels of escaping by recursively replacing \\n with \n
            let processedValue = item[field];
            while (processedValue.includes('\\n')) {
              processedValue = processedValue.replace(/\\n/g, '\n');
            }
            item[field] = processedValue;
          }
        });
        return item;
      });
    }

    return {
      data: transformedData,
      total: data?.pagination?.total,
    };
  },

  getOne: async ({ resource, id }) => {
    const url = `/${resource}/${id}`;
    const { data } = await apiClient.get(url);

    // Transform data for form fields
    let transformedData = data?.data;
    if (resource === 'patients' && transformedData?.type) {
      // Convert comma-separated string back to array for form
      transformedData = {
        ...transformedData,
        type: typeof transformedData.type === 'string'
          ? transformedData.type.split(',').filter(Boolean)
          : transformedData.type
      };
    }

    // Transform treatments data for form fields
    if (resource === 'treatments' && transformedData?.body_part) {
      // Convert comma-separated string back to array for form
      transformedData = {
        ...transformedData,
        body_part: typeof transformedData.body_part === 'string'
          ? transformedData.body_part.split(',').map((s: string) => s.trim()).filter(Boolean)
          : transformedData.body_part
      };
    }

    if (resource === 'treatments' && transformedData?.treatment_name != null) {
      transformedData = {
        ...transformedData,
        treatment_name:
          typeof transformedData.treatment_name === 'string'
            ? transformedData.treatment_name
                .split(',')
                .map((s: string) => s.trim())
                .filter(Boolean)
            : transformedData.treatment_name,
      };
    }

    // Transform treatments power_duration data for form fields
    if (resource === 'treatments' && transformedData?.power_duration) {
      // Convert comma-separated string back to array for form
      transformedData = {
        ...transformedData,
        power_duration: typeof transformedData.power_duration === 'string'
          ? transformedData.power_duration.split(',').filter(Boolean)
          : transformedData.power_duration
      };
    }

    // Transform leads interest data for form fields
    if (resource === 'leads' && transformedData?.interest) {
      // Convert comma-separated string back to array for form
      transformedData = {
        ...transformedData,
        interest: typeof transformedData.interest === 'string'
          ? transformedData.interest.split(',').filter(Boolean)
          : transformedData.interest
      };
    }

    // Transform text fields to handle escaped newlines
    if (transformedData) {
      const textFields = ['notes', 'lab_report', 'today_procedure', 'remark', 'description'];
      textFields.forEach(field => {
        if (transformedData[field] && typeof transformedData[field] === 'string') {
          // Handle multiple levels of escaping by recursively replacing \\n with \n
          let processedValue = transformedData[field];
          while (processedValue.includes('\\n')) {
            processedValue = processedValue.replace(/\\n/g, '\n');
          }
          transformedData[field] = processedValue;
        }
      });
    }

    return {
      data: transformedData,
    };
  },

  create: async ({ resource, variables }) => {
    const url = `/${resource}`;
    // Format datetime and date fields before sending to API
    const formattedVariables = Object.entries(variables as Record<string, any>).reduce((acc, [key, value]) => {
      if ((key.includes('follow_up_date') || key.includes('scheduled_datetime') || key.includes('date') || key.includes('expected_delivery_date') || key.includes('prescribed_at')) && value) {
        acc[key] = dayjs(value as string | Date).utc().format('YYYY-MM-DD HH:mm:ss');
      } else if (key.includes('birth_date') && value) {
        acc[key] = dayjs(value as string | Date).utc().format('YYYY-MM-DD');
      } else if (key === 'type' && Array.isArray(value)) {
        // Transform patient type array to comma-separated string for MySQL SET type
        acc[key] = value.join(',');
      } else if (key === 'body_part' && Array.isArray(value)) {
        // Transform body part array to comma-separated string for backend storage
        acc[key] = value.join(',');
      } else if (key === 'treatment_name' && Array.isArray(value)) {
        acc[key] = value
          .map((v: unknown) => String(v ?? '').trim())
          .filter(Boolean)
          .join(',');
      } else if (key === 'power_duration' && Array.isArray(value)) {
        // Transform power duration array to comma-separated string for backend storage
        acc[key] = value.join(',');
      } else if (key === 'interest' && Array.isArray(value)) {
        // Transform interest array to comma-separated string for backend storage
        acc[key] = value.join(',');
      } else if (['notes', 'lab_report', 'today_procedure', 'remark', 'description'].includes(key) && typeof value === 'string') {
        // Handle text fields - convert \n strings back to actual newlines for storage
        acc[key] = value.replace(/\\n/g, '\n');
      } else {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, any>);

    const { data } = await apiClient.post(url, formattedVariables);
    return {
      data,
    };
  },

  update: async ({ resource, id, variables }) => {
    const url = `/${resource}/${id}`;
    // Format datetime and date fields before sending to API
    const formattedVariables = Object.entries(variables as Record<string, any>).reduce((acc, [key, value]) => {
      if ((key.includes('follow_up_date') || key.includes('scheduled_datetime') || key.includes('date') || key.includes('expected_delivery_date') || key.includes('prescribed_at')) && value) {
        acc[key] = dayjs(value as string | Date).utc().format('YYYY-MM-DD HH:mm:ss');
      } else if (key.includes('birth_date') && value) {
        acc[key] = dayjs(value as string | Date).utc().format('YYYY-MM-DD');
      } else if (key === 'type' && Array.isArray(value)) {
        // Transform patient type array to comma-separated string for MySQL SET type
        acc[key] = value.join(',');
      } else if (key === 'body_part' && Array.isArray(value)) {
        // Transform body part array to comma-separated string for backend storage
        acc[key] = value.join(',');
      } else if (key === 'treatment_name' && Array.isArray(value)) {
        acc[key] = value
          .map((v: unknown) => String(v ?? '').trim())
          .filter(Boolean)
          .join(',');
      } else if (key === 'power_duration' && Array.isArray(value)) {
        // Transform power duration array to comma-separated string for backend storage
        acc[key] = value.join(',');
      } else if (key === 'interest' && Array.isArray(value)) {
        // Transform interest array to comma-separated string for backend storage
        acc[key] = value.join(',');
      } else if (['notes', 'lab_report', 'today_procedure', 'remark', 'description'].includes(key) && typeof value === 'string') {
        // Handle text fields - convert \n strings back to actual newlines for storage
        acc[key] = value.replace(/\\n/g, '\n');
      } else {
        acc[key] = value;
      }
      return acc;
    }, {} as Record<string, any>);

    const { data } = await apiClient.put(url, formattedVariables);
    return {
      data,
    };
  },

  updateMany: async ({ resource, ids, variables }) => {
    const response = await Promise.all(
      ids.map(id => apiClient.put(`/${resource}/${id}`, variables))
    );
    const data = response.map(({ data }: any) => data);
    return {
      data,
    };
  },

  createMany: async ({ resource, variables }) => {
    const response = await Promise.all(
      variables.map(variable => apiClient.post(`/${resource}`, variable))
    );
    const data = response.map(({ data }: any) => data);
    return {
      data,
    };
  },

  deleteOne: async ({ resource, id }) => {
    const url = `/${resource}/${id}`;
    const { data } = await apiClient.delete(url);
    return {
      data,
    };
  },

  deleteMany: async ({ resource, ids }) => {
    const response = await Promise.all(
      ids.map(id => apiClient.delete(`/${resource}/${id}`))
    );
    const data = response.map(({ data }: any) => data);
    return {
      data,
    };
  },

  getMany: async ({ resource, ids }) => {
    const response = await Promise.all(
      ids.map(id => apiClient.get(`/${resource}/${id}`))
    );
    const data = response.map(({ data }: any) => data);
    return {
      data,
    };
  },

  getApiUrl: () => {
    return import.meta.env.VITE_API_URL || 'https://api.fake-rest.refine.dev';
  },

  custom: async ({
    url,
    method,
    filters,
    sorters,
    payload,
    query,
    headers,
  }) => {
    let requestUrl = `${url}?`;

    if (query) {
      requestUrl = `${requestUrl}${new URLSearchParams(query).toString()}`;
    }

    if (headers) {
      apiClient.defaults.headers.common = {
        ...apiClient.defaults.headers.common,
        ...headers,
      };
    }

    let axiosResponse;
    switch (method) {
      case 'put':
      case 'post':
      case 'patch':
        axiosResponse = await apiClient[method](url, payload);
        break;
      case 'delete':
        axiosResponse = await apiClient.delete(url, {
          data: payload,
        });
        break;
      default:
        axiosResponse = await apiClient.get(requestUrl);
        break;
    }

    const { data } = axiosResponse;

    return Promise.resolve({ data });
  },
};
